import {useCallback, useMemo} from 'react';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';

type AuthMode = 'signup' | 'login';

type AuthStepStartedEventParams = {
  flow_id: string;
  process_id: string;
  provider: string;
  request_type: string;
  input_value: string;
  number_of_required_fields?: number;
  number_of_fields_completed?: number;
};

type AuthStepEndedEventParams = {
  flow_id: string;
  process_id: string;
  provider: string;
  request_type: string;
  input_value: string;
  reason: string;
  error?: string | null;
};

function useTrackAuthStepEvent(authMode: AuthMode) {
  const started = useCallback(
    ({
      flow_id,
      process_id,
      provider,
      request_type,
      input_value,
      number_of_required_fields = 0,
      number_of_fields_completed = 0,
    }: AuthStepStartedEventParams) => {
      try {
        const eventName =
          authMode === 'signup'
            ? eventNames.SIGNUP_STEP_STARTED
            : eventNames.LOGIN_STEP_STARTED;

        const eventData: Record<string, any> = {
          flow_id,
          process_id,
          request_type,
          input_value,
        };

        // Add provider field based on authMode
        if (authMode === 'signup') {
          eventData.signup_provider = provider;
        } else {
          eventData.login_provider = provider;
        }

        // Add number_of_required_fields and number_of_fields_completed only for signup
        if (authMode === 'signup') {
          eventData.number_of_required_fields = number_of_required_fields;
          eventData.number_of_fields_completed = number_of_fields_completed;
        }

        AnalyticsService.dispatchEvent(eventName, eventData);
      } catch (error) {
        console.error('Error tracking auth step started event', error);
      }
    },
    [authMode]
  );

  const ended = useCallback(
    ({
      flow_id,
      process_id,
      provider,
      request_type,
      input_value,
      reason,
      error = null,
    }: AuthStepEndedEventParams) => {
      try {
        const eventName =
          authMode === 'signup'
            ? eventNames.SIGNUP_STEP_ENDED
            : eventNames.LOGIN_STEP_ENDED;

        const eventData: Record<string, any> = {
          flow_id,
          process_id,
          request_type,
          input_value,
          reason,
          error,
        };

        // Add provider field based on authMode
        if (authMode === 'signup') {
          eventData.signup_provider = provider;
        } else {
          eventData.login_provider = provider;
        }

        AnalyticsService.dispatchEvent(eventName, eventData);
      } catch (e) {
        console.error('Error tracking auth step ended event', e);
      }
    },
    [authMode]
  );

  return useMemo(() => ({started, ended}), [started, ended]);
}

export default useTrackAuthStepEvent;
